import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Breadcrumbs,
  Card,
  CardContent,
  CardActions,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  LinearProgress,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Public as PublicIcon,
  Science as ScienceIcon,
  CameraAlt as CameraIcon,
  Timer as TimerIcon,
  FilterList as FilterIcon,
  Settings as SettingsIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Info as InfoIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  NavigateNext as NavigateNextIcon,
  FolderOpen as FolderOpenIcon,
  CloudUpload as CloudUploadIcon,
  Replay as RetryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../../services/apiService';
import { websocketService } from '../../services/websocketService';
import { astronomyService } from '../../services/astronomyService';
import { computePositionFromElements } from '../../utils/orbitalMechanics';
import ExecutionHistory from '../../components/ExecutionHistory';
import ObservationLog from '../../components/ObservationLog';
import { withAuthToken, filePreviewUrl } from '../../utils/fileUtils';
import LazyImage from '../../components/common/LazyImage';
import FileCalibrationChip from '../../components/files/FileCalibrationChip';
import ProcessingFailureChip from '../../components/files/ProcessingFailureChip';
import PushExternallyDialog from '../../components/PushExternallyDialog';
import ImageSelectionBar from '../../components/files/ImageSelectionBar';
import ZipDownloadButton from '../../components/files/ZipDownloadButton';
import { openSkyView } from '../../utils/skyView';
import { getAccessToken, getRefreshToken, updateAccessToken } from '../../services/authStorage';
import { useImageSelection } from '../../hooks/useImageSelection';
import { isUncalibratedResponse, markUncalibratedName, buildUncalibratedWarning } from '../../utils/calibratedDownload';
import { isObservationEditable } from '../../utils/observationEditable';
import { buildQualityRows } from '../../utils/qualityMetrics';

// Helper function to format dates in UTC with 24-hour time
function formatUTC(dateString, formatStr = 'PPP HH:mm:ss') {
  if (!dateString) return 'N/A';
  try {
    // FITS timestamps (and other astronomical data) use ISO 8601 format without timezone
    // and should be interpreted as UTC. If the string doesn't have a timezone indicator,
    // append 'Z' to explicitly mark it as UTC before parsing.
    let utcDateString = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      utcDateString = dateString + 'Z';
    }

    // Parse the ISO string and format it in UTC timezone
    // Use 24-hour format (HH:mm:ss) instead of 12-hour (pp)
    // PPP = Long date format, HH:mm:ss = 24-hour time
    return formatInTimeZone(parseISO(utcDateString), 'UTC', formatStr) + ' UTC';
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}

const PROVIDER_LABELS = { dropbox: 'Dropbox', gcs: 'Google Cloud Storage', gdrive: 'Google Drive', 'aws-s3': 'AWS S3', sftp: 'SFTP/SCP' };
const PROVIDER_COLORS = { dropbox: 'primary', gcs: 'secondary', gdrive: 'success', 'aws-s3': 'warning', sftp: 'info' };

// Group an observation's external transfers into "deliveries": the automatic
// delivery that ran as part of the initial observation (transfers with no
// push_batch_id), and each on-demand Push Externally (grouped by push_batch_id),
// split per destination. Each delivery carries where it went, who requested it,
// when, and per-status counts — so the card shows the full history in one place.
function buildDeliveries(configs, transfers) {
  const configById = new Map((configs || []).map(c => [String(c._id), c]));
  const groups = new Map();
  for (const t of (transfers || [])) {
    const batch = t.push_batch_id || null;
    const key = `${batch || 'initial'}::${String(t.config_id)}`;
    if (!groups.has(key)) {
      groups.set(key, { key, origin: batch ? 'on-demand' : 'initial', configId: String(t.config_id), transfers: [] });
    }
    groups.get(key).transfers.push(t);
  }
  const list = [];
  for (const g of groups.values()) {
    const cfg = configById.get(g.configId) || {};
    const ts = g.transfers;
    const sample = ts.find(t => t.destination_path) || ts[0] || {};
    const dir = sample.destination_path
      ? sample.destination_path.slice(0, sample.destination_path.lastIndexOf('/'))
      : (cfg.destination_path || '');
    const requestedAt = ts.reduce((min, t) => (t.created_at && (!min || new Date(t.created_at) < new Date(min))) ? t.created_at : min, null);
    list.push({
      key: g.key,
      origin: g.origin,
      displayName: cfg.display_name || 'External destination',
      provider: cfg.provider || sample.provider,
      directory: dir,
      total: ts.length,
      completed: ts.filter(t => t.status === 'completed').length,
      active: ts.filter(t => ['pending', 'in_progress', 'paused'].includes(t.status)).length,
      cancelled: ts.filter(t => t.status === 'cancelled').length,
      failedTransfers: ts.filter(t => t.status === 'failed'),
      requestedBy: (ts.find(t => t.requested_by_label) || {}).requested_by_label || null,
      requestedAt,
    });
  }
  // Initial delivery first, then on-demand pushes oldest→newest.
  list.sort((a, b) => (a.origin !== b.origin)
    ? (a.origin === 'initial' ? -1 : 1)
    : (new Date(a.requestedAt || 0) - new Date(b.requestedAt || 0)));
  return list;
}

// Convert decimal degrees to sexagesimal format
function decimalToSexagesimal(decimal, isRA = false) {
  if (decimal === null || decimal === undefined) return 'N/A';
  
  const absolute = Math.abs(decimal);
  
  if (isRA) {
    // RA: Convert degrees to hours first
    const hours = absolute / 15; // Convert degrees to hours
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toFixed(2).padStart(5, '0')}s`;
  } else {
    // Dec: Keep in degrees
    const sign = decimal < 0 ? '-' : '+';
    const d = Math.floor(absolute);
    const m = Math.floor((absolute - d) * 60);
    const s = ((absolute - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}° ${m.toString().padStart(2, '0')}' ${s.toFixed(1).padStart(4, '0')}"`;
  }
}


const ObservationDetail = ({ embedded = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [observation, setObservation] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [calibrateError, setCalibrateError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailReqRef = React.useRef(null);

  // Open a file's detail modal. The lean list item shows scalars/preview
  // instantly; heavy fields (fits_header/quality_metrics/plate_solve_result)
  // are fetched separately and merged in. Guard against races: if the user
  // opens file A then quickly file B, A's response must not overwrite B.
  const openFileDetail = async (file) => {
    setSelectedFile(file);
    setDetailsOpen(true);
    const fid = file.file_id || file._id;
    detailReqRef.current = fid;
    if (!fid) return;
    setDetailLoading(true);
    let d = {};
    try {
      const resp = await apiService.getUserFile(id, fid);
      d = resp?.data || {};
    } catch (_) {
      // keep basic view on failure
    }
    if (detailReqRef.current !== fid) return; // a newer file was opened
    setSelectedFile(prev => (prev && (prev.file_id || prev._id) === fid)
      ? { ...prev, fits_header: d.fits_header, quality_metrics: d.quality_metrics, plate_solve_result: d.plate_solve_result }
      : prev);
    setDetailLoading(false);
  };
  const [calculatedVisibilities, setCalculatedVisibilities] = useState({});
  const [executionHistoryOpen, setExecutionHistoryOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [rebuildPreviewsOpen, setRebuildPreviewsOpen] = useState(false);
  const [rebuildPreviewsBusy, setRebuildPreviewsBusy] = useState(false);
  const [rebuildPreviewsCalibratedOnly, setRebuildPreviewsCalibratedOnly] = useState(true);
  const reloadTimerRef = React.useRef(null);
  const reloadFirstScheduledRef = React.useRef(null); // wall-clock of first event in current debounce window
  const completionReloadRef = React.useRef(false); // true when waiting for completion reload
  const rebuildPollTimerRef = React.useRef(null); // polling timer active during preview rebuild
  // Monotonic sequence so an older in-flight getUserFiles response can't
  // clobber state that a later request has already written (#614). During
  // a rebuild, WS file_updated events + polling fire a lot of concurrent
  // loads; without ordering, stale responses would stomp on fresh ones.
  const loadSeqRef = React.useRef(0);
  const lastAppliedSeqRef = React.useRef(0);
  const [storageInfo, setStorageInfo] = useState({ configs: [], transfers: [], loading: true });
  const [pushDialogOpen, setPushDialogOpen] = useState(false);
  const [storageReloadKey, setStorageReloadKey] = useState(0);
  const [obsFiles, setObsFiles] = useState([]);
  // Per-file_type breakdown from the user-files endpoint (#1040) so the header can
  // show raw images vs derived (calibrated/…) separately instead of one combined
  // total that conflates them.
  const [fileTypeCounts, setFileTypeCounts] = useState(null);
  // RAW-only count/bytes + the server's effective ZIP caps (#686) so the ZIP button
  // gates precisely (the file list reports file_type:'fits' for every row).
  const [zipCaps, setZipCaps] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({
    status: 'idle', // idle | fetching | downloading | complete | error | cancelled
    current: 0,
    total: 0,
    filename: '',
    error: ''
  });
  const downloadAbortController = React.useRef(null);
  const sessionKeepAliveInterval = React.useRef(null);
  const cachedObservatoryLocation = React.useRef(null);
  const [constraintStatus, setConstraintStatus] = useState(null);
  const [constraintLoading, setConstraintLoading] = useState(false);

  // Session keep-alive during long downloads to prevent token expiration
  // Proactively refreshes the token before it expires
  const startSessionKeepAlive = () => {
    if (sessionKeepAliveInterval.current) return; // Already running
    console.log('[Download] Starting session keep-alive');

    // Proactively refresh the token
    const refreshSession = async () => {
      try {
        console.log('[Download] Proactively refreshing token...');
        const refreshToken = getRefreshToken();
        if (!refreshToken) {
          console.warn('[Download] No refresh token available');
          return;
        }

        const response = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            updateAccessToken(data.data.accessToken, data.data.refreshToken);
            console.log('[Download] Token refreshed successfully');
          }
        } else {
          console.warn('[Download] Token refresh failed:', response.status);
        }
      } catch (error) {
        console.warn('[Download] Token refresh error:', error.message);
      }
    };

    // Refresh immediately, then every 5 minutes
    refreshSession();
    sessionKeepAliveInterval.current = setInterval(refreshSession, 5 * 60 * 1000);
  };

  const stopSessionKeepAlive = () => {
    if (sessionKeepAliveInterval.current) {
      console.log('[Download] Stopping session keep-alive');
      clearInterval(sessionKeepAliveInterval.current);
      sessionKeepAliveInterval.current = null;
    }
  };

  // Cleanup keep-alive on unmount
  React.useEffect(() => {
    return () => stopSessionKeepAlive();
  }, []);

  // Determine back navigation based on where user came from
  const fromPage = location?.state?.from;
  const backRoute = fromPage === 'dashboard' ? '/dashboard'
    : fromPage === 'admin' ? '/admin/observations'
    : '/observations';
  const backLabel = fromPage === 'dashboard' ? 'Back to Dashboard'
    : fromPage === 'admin' ? 'Back to Admin Observations'
    : 'Back to Observations';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadObservation();
  }, [id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Debounced reload — coalesces rapid WebSocket events into a single API call.
    // Trailing debounce with a max-wait: during a sustained burst (e.g. Rebuild
    // Previews fires a file_updated every ~1s per file, which keeps resetting
    // the 2s trailing timer), the max-wait forces a reload at least every 5s
    // so users see progress in-stream instead of only at the end (#614).
    // Skips if a completion reload is already scheduled (longer delay takes priority)
    const RELOAD_DEBOUNCE_MS = 2000;
    const RELOAD_MAX_WAIT_MS = 5000;
    const scheduleReload = () => {
      if (completionReloadRef.current) return; // completion reload pending, don't override

      const now = Date.now();
      if (reloadFirstScheduledRef.current === null) {
        reloadFirstScheduledRef.current = now;
      }
      const waited = now - reloadFirstScheduledRef.current;

      if (waited >= RELOAD_MAX_WAIT_MS) {
        // Sustained event stream — fire now, reset the window.
        if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
        reloadTimerRef.current = null;
        reloadFirstScheduledRef.current = null;
        loadObservation({ silent: true });
        return;
      }

      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadTimerRef.current = setTimeout(() => {
        reloadTimerRef.current = null;
        reloadFirstScheduledRef.current = null;
        loadObservation({ silent: true });
      }, RELOAD_DEBOUNCE_MS);
    };

    // Reload and check if all files and previews are present, retry if not
    let completionRetryCount = 0;
    const MAX_COMPLETION_RETRIES = 5;

    const completionReload = async () => {
      reloadTimerRef.current = null;
      reloadFirstScheduledRef.current = null;
      completionReloadRef.current = false;
      await loadObservation({ silent: true });

      // Check if files or previews are still missing — retry if so
      setObservation(prev => {
        if (!prev) return prev;
        const files = prev.files || [];
        const expectedCount = prev.actual_file_count || prev.expected_file_count || 0;
        const missingFiles = expectedCount > 0 && files.length < expectedCount;
        const missingPreviews = files.some(f => !f.preview_url && !f.metadata?.preview_url);

        if ((missingFiles || missingPreviews) && completionRetryCount < MAX_COMPLETION_RETRIES) {
          completionRetryCount++;
          const delay = Math.min(5000 * completionRetryCount, 15000);
          console.log(`Completion reload retry ${completionRetryCount}/${MAX_COMPLETION_RETRIES}: ` +
            `${missingFiles ? `${files.length}/${expectedCount} files` : 'all files present'}, ` +
            `${missingPreviews ? 'some previews missing' : 'all previews present'}, ` +
            `next retry in ${delay / 1000}s`);
          completionReloadRef.current = true;
          reloadTimerRef.current = setTimeout(completionReload, delay);
        }
        return prev;
      });
    };

    // Schedule initial completion reload
    const scheduleCompletionReload = () => {
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      reloadFirstScheduledRef.current = null;
      completionRetryCount = 0;
      completionReloadRef.current = true;
      reloadTimerRef.current = setTimeout(completionReload, 5000);
    };

    // Set up WebSocket listener for observation updates
    const handleObservationUpdate = async (data) => {
      const observationId = data.observation_id || data._id || data.id;

      if (observationId === id) {
        console.log('Observation update received:', data);
        // Patch status in-place if available
        if (data.status) {
          setObservation(prev => prev ? { ...prev, status: data.status } : prev);
        }

        const completionStatuses = ['completed', 'completed_series', 'acquisition_complete'];
        if (completionStatuses.includes(data.status)) {
          // On observation complete, start retry loop until all files and previews present
          scheduleCompletionReload();
        } else if (data.type === 'progress_update') {
          setObservation(prev => prev ? {
            ...prev,
            ...(data.frames_captured !== undefined && { captured_file_count: data.frames_captured }),
            ...(data.progress_percent !== undefined && { completion_percentage: data.progress_percent / 100 }),
          } : prev);
        } else {
          // Debounced reload for non-progress updates (file uploads, status changes, etc.)
          scheduleReload();
        }
      }
    };

    // Handle file_updated events (e.g., processing complete with new preview)
    const handleFileUpdated = () => {
      // Any file update triggers a debounced reload to pick up new previews
      scheduleReload();
    };

    // Handle observation_log_entry events — refresh constraint status
    const handleLogEntry = (data) => {
      const observationId = data.observation_id;
      if (observationId === id) {
        console.log('Observation log entry received, refreshing constraint status:', data);
        setObservation(prev => {
          if (prev) loadConstraintStatus(prev);
          return prev;
        });
      }
    };

    // Subscribe to observation updates
    websocketService.onObservationUpdate(handleObservationUpdate);
    websocketService.on('file_updated', handleFileUpdated);
    websocketService.on('observation_log_entry', handleLogEntry);

    // Cleanup on unmount
    return () => {
      websocketService.off('observationUpdate', handleObservationUpdate);
      websocketService.off('file_updated', handleFileUpdated);
      websocketService.off('observation_log_entry', handleLogEntry);
      if (reloadTimerRef.current) clearTimeout(reloadTimerRef.current);
      if (rebuildPollTimerRef.current) clearTimeout(rebuildPollTimerRef.current);
      completionReloadRef.current = false;
      reloadFirstScheduledRef.current = null;
    };
  }, [id]);

  // Load external storage info. Shows a status row for every destination that
  // has transfers for this observation — both those attached at submission
  // (external_storage_destinations) AND any pushed on-demand via "Push
  // Externally" to a destination that wasn't attached.
  useEffect(() => {
    if (!observation?._id) {
      setStorageInfo({ configs: [], transfers: [], loading: false });
      return;
    }
    const destIds = (observation.external_storage_destinations || []).map(String);
    const loadStorageInfo = async () => {
      try {
        setStorageInfo(prev => ({ ...prev, loading: true }));
        const transfersRes = await apiService.getExternalStorageTransfers({ observation_id: observation._id, limit: 2000 });
        const transfers = transfersRes.data?.transfers || transfersRes.data || [];

        // Destinations to show = attached ∪ any config that has a transfer here.
        const transferConfigIds = [...new Set(transfers.map(t => t.config_id).filter(Boolean).map(String))];
        const relevantIds = [...new Set([...destIds, ...transferConfigIds])];
        if (relevantIds.length === 0) {
          setStorageInfo({ configs: [], transfers, loading: false });
          return;
        }

        // Resolve display details from the configs the user can list.
        const availableRes = await apiService.getAvailableStorageConfigs();
        const allConfigs = [];
        if (availableRes.data?.personal) {
          allConfigs.push(...availableRes.data.personal);
        }
        if (availableRes.data?.organization) {
          for (const group of Object.values(availableRes.data.organization)) {
            allConfigs.push(...(group.configs || []));
          }
        }
        if (availableRes.data?.project) {
          for (const group of Object.values(availableRes.data.project)) {
            allConfigs.push(...(group.configs || []));
          }
        }
        const byId = new Map(allConfigs.map(c => [String(c._id), c]));

        const configs = relevantIds.map(cid => {
          const found = byId.get(cid);
          const attached = destIds.includes(cid);
          if (found) return { ...found, _attached: attached };
          // Fallback: a destination the user can no longer list (e.g. an org
          // config they've lost access to). Synthesize minimal display details
          // from a transfer's denormalized snapshot so status still renders.
          const t = transfers.find(tr => String(tr.config_id) === cid);
          return {
            _id: cid,
            display_name: 'External destination',
            provider: t?.provider,
            destination_path: t?.path_root,
            custom_path: t?.path_template,
            _attached: attached,
          };
        });

        setStorageInfo({ configs, transfers, loading: false });
      } catch (err) {
        console.error('Failed to load external storage info:', err);
        setStorageInfo({ configs: [], transfers: [], loading: false });
      }
    };
    loadStorageInfo();
  }, [observation?._id, observation?.external_storage_destinations, storageReloadKey]);

  // Poll transfer status while any transfer is still active so the card reflects
  // completions live. The interval is created only while work is outstanding and
  // torn down once every transfer is terminal (dep flips false).
  const hasActiveTransfers = (storageInfo.transfers || []).some(
    t => t.status === 'pending' || t.status === 'in_progress' || t.status === 'paused'
  );
  useEffect(() => {
    if (!hasActiveTransfers || !observation?._id) return undefined;
    const interval = setInterval(async () => {
      try {
        const res = await apiService.getExternalStorageTransfers({ observation_id: observation._id, limit: 2000 });
        setStorageInfo(prev => ({ ...prev, transfers: res.data?.transfers || res.data || [] }));
      } catch {
        // transient; keep polling
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [hasActiveTransfers, observation?._id]);

  const calculateVisibilityForTarget = async (target, targetIndex, obs) => {
    try {
      // Get observatory location - try from observation or use default
      let latitude = 40.0; // Default latitude
      let longitude = -105.0; // Default longitude
      let altitude = 0;
      
      // Try to get location from assigned observatory
      if (obs.assigned_observatory?.location) {
        const loc = obs.assigned_observatory.location;
        latitude = loc.latitude || latitude;
        longitude = loc.longitude || longitude;
        altitude = loc.altitude_meters || 0;
      } else if (cachedObservatoryLocation.current) {
        // Use cached observatory location
        latitude = cachedObservatoryLocation.current.latitude || latitude;
        longitude = cachedObservatoryLocation.current.longitude || longitude;
        altitude = cachedObservatoryLocation.current.altitude_meters || 0;
      } else {
        // Fetch default observatory location and cache it
        try {
          const observatories = await apiService.getObservatories();
          if (observatories.data && observatories.data.length > 0) {
            const defaultObs = observatories.data[0];
            if (defaultObs.location) {
              cachedObservatoryLocation.current = defaultObs.location;
              latitude = defaultObs.location.latitude || latitude;
              longitude = defaultObs.location.longitude || longitude;
              altitude = defaultObs.location.altitude_meters || 0;
            }
          }
        } catch (err) {
          console.log('Could not fetch observatory location, using defaults');
        }
      }
      
      // For targets with orbital elements, compute current RA/Dec
      let targetRa = target.ra;
      let targetDec = target.dec;
      
      if (target.orbital_elements) {
        const position = computePositionFromElements(target.orbital_elements, new Date());
        targetRa = position.ra;
        targetDec = position.dec;
        console.log(`Computed position for ${target.name}: RA=${targetRa}, Dec=${targetDec}`);
      }
      
      // Calculate rise, transit, set times
      const visibility = astronomyService.calculateRiseSetTimes(
        targetRa,
        targetDec,
        latitude,
        longitude,
        altitude,
        new Date()
      );
      
      // Format the visibility data
      const formattedVisibility = {
        rise_time: visibility.riseTime,
        transit_time: visibility.transitTime,
        set_time: visibility.setTime,
        transit_altitude: visibility.maxAltitude,
        is_circumpolar: visibility.isCircumpolar,
        never_rises: visibility.neverRises
      };
      
      // Calculate hours visible if we have rise and set times
      if (visibility.riseTime && visibility.setTime) {
        const visibleMs = visibility.setTime - visibility.riseTime;
        formattedVisibility.hours_visible = visibleMs / (1000 * 60 * 60);
      }
      
      // Determine best observation time (around transit if visible)
      if (visibility.transitTime && !visibility.neverRises) {
        formattedVisibility.best_observation_time = visibility.transitTime;
      }
      
      setCalculatedVisibilities(prev => ({
        ...prev,
        [targetIndex]: formattedVisibility
      }));
      console.log(`Calculated visibility for target ${targetIndex}:`, formattedVisibility);
    } catch (err) {
      console.error('Error calculating visibility:', err);
    }
  };

  const loadConstraintStatus = async (obs) => {
    // Only fetch constraints for observations that can still be scheduled
    const schedulableStatuses = ['pending', 'waiting_next', 'assigned'];
    if (!schedulableStatuses.includes(obs.status)) {
      setConstraintStatus(null);
      return;
    }
    try {
      setConstraintLoading(true);
      const response = await apiService.getDispatchStatus(null, obs.status);
      if (response.success && response.data?.observations) {
        const match = response.data.observations.find(o => o.id === obs._id);
        setConstraintStatus(match || null);
      }
    } catch (err) {
      console.error('Failed to load constraint status:', err);
    } finally {
      setConstraintLoading(false);
    }
  };

  const loadObservation = async ({ silent = false, continuePolling = false } = {}) => {
    const seq = ++loadSeqRef.current;
    // When a stale load is discarded we still need to keep the poll loop
    // alive, so track fresh files here and make the decision in finally.
    let freshFiles = null;
    try {
      if (!silent) setLoading(true);
      setError(null);

      const response = await apiService.getObservation(id);
      if (seq < lastAppliedSeqRef.current) {
        return; // stale; finally still schedules next poll
      }
      lastAppliedSeqRef.current = seq;
      setObservation(response.data);

      // Load files with calibratability info from user-files endpoint
      try {
        const filesResponse = await apiService.getUserFiles({ observationId: id });
        if (seq < lastAppliedSeqRef.current) {
          return; // stale; finally still schedules next poll
        }
        lastAppliedSeqRef.current = seq;
        const files = filesResponse.data?.files || filesResponse.files || [];
        setObsFiles(files);
        setFileTypeCounts(filesResponse.data?.file_type_counts || null);
        {
          const d = filesResponse.data || {};
          setZipCaps({
            // If the server sent counts but no 'raw' key, that's a genuine 0 raw frames
            // (disable the button); if it sent no counts at all, leave null = unknown so
            // the button falls back to the full file count rather than disabling.
            rawCount: d.file_type_counts ? (d.file_type_counts.raw ?? 0) : null,
            rawBytes: d.file_type_bytes?.raw ?? 0,
            maxFiles: d.zip_limits?.max_files,
            maxBytes: d.zip_limits?.max_bytes,
          });
        }
        freshFiles = files;
      } catch (filesErr) {
        console.warn('Failed to load observation files:', filesErr.message);
      }

      // Load constraint status for schedulable observations
      loadConstraintStatus(response.data);

      // Calculate visibility for all targets
      const targets = response.data.targets || (response.data.target ? [response.data.target] : []);
      targets.forEach((target, index) => {
        if (target && (!target.visibility || target.orbital_elements)) {
          calculateVisibilityForTarget(target, index, response.data);
        }
      });

      // Load project data if observation has a project_id.
      // The observations GET endpoint populates project_id into a subdoc
      // ({_id, name, code, type, status, scientific_goals}) — every field the
      // Project card below renders — and it is visible to anyone who can see
      // the observation. Use it as-is rather than calling GET /projects/:id,
      // which requires project membership: a viewer who reaches the
      // observation through their observatory but isn't on the project gets a
      // 403, and the server audits that into their activity history as
      // "denied access permission" even though the UI shrugged it off.
      const rawProjectId = response.data.project_id;
      if (rawProjectId && typeof rawProjectId === 'object') {
        setProject(rawProjectId);
      } else if (rawProjectId) {
        // Bare id (not populated) — a fetch is the only way to name it.
        try {
          const projectResponse = await apiService.getProject(rawProjectId);
          setProject(projectResponse.data);
        } catch (projectErr) {
          console.error('Failed to load project:', projectErr);
          // Don't fail the whole load if project fails
        }
      }
    } catch (err) {
      console.error('Failed to load observation:', err);
      setError('Failed to load observation details');
    } finally {
      if (!silent) setLoading(false);
      // Schedule next poll here — runs even if the body short-circuited
      // because this load was stale (seq guard hit), so polling can't die.
      if (continuePolling) {
        if (rebuildPollTimerRef.current) clearTimeout(rebuildPollTimerRef.current);
        let done = false;
        if (freshFiles) {
          const missing = freshFiles.filter(f => !f.preview_url).length;
          const queued = freshFiles.some(f => f.processing_status === 'queued');
          done = missing === 0 && !queued;
        }
        if (done) {
          rebuildPollTimerRef.current = null;
        } else {
          rebuildPollTimerRef.current = setTimeout(() => {
            loadObservation({ silent: true, continuePolling: true });
          }, 3000);
        }
      }
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      pending: 'info',
      scheduled: 'info',
      assigned: 'warning',
      in_progress: 'warning',
      acquisition_complete: 'warning',
      completed: 'success',
      completed_series: 'success',
      partially_completed: 'secondary',
      failed: 'error',
      aborted: 'error',
      cancelled: 'default',
      waiting_next: 'info',
      paused: 'warning',
      suspended: 'warning',
      expired: 'error',
    };
    return statusColors[status] || 'default';
  };

  const isCancellable = (status) => {
    return ['pending', 'scheduled', 'assigned', 'in_progress', 'paused'].includes(status);
  };

  const handleCancelObservation = async () => {
    try {
      setCancelling(true);
      await apiService.cancelObservation(observation._id, cancelReason);
      setCancelDialogOpen(false);
      setCancelReason('');
      // Reload observation to get updated status
      await loadObservation();
    } catch (err) {
      console.error('Failed to cancel observation:', err);
      setError('Failed to cancel observation');
    } finally {
      setCancelling(false);
    }
  };

  // Check if File System Access API is supported
  // Requires secure context (HTTPS or localhost)
  const supportsDirectoryPicker = 'showDirectoryPicker' in window;

  // Multi-select subset download is folder-picker-only (see #1275). Gate the whole
  // feature on it. renderedFiles must match the exact list the grid maps over below.
  const multiSelect = supportsDirectoryPicker;
  const renderedFiles = obsFiles.length > 0 ? obsFiles : (observation?.files || []);
  const sel = useImageSelection(renderedFiles);

  // Esc clears the multi-select (Chrome-only feature).
  useEffect(() => {
    if (!multiSelect) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') sel.clear(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [multiSelect, sel.clear]);

  // Debug: log API support status
  useEffect(() => {
    console.log('File System Access API support:', {
      supportsDirectoryPicker,
      isSecureContext: window.isSecureContext,
      protocol: window.location.protocol,
      hostname: window.location.hostname
    });
  }, [supportsDirectoryPicker]);

  // Detect if browser is Chrome (has "keep" prompt issue for multiple downloads)
  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg|OPR/.test(navigator.userAgent);

  // Fallback download for browsers without File System Access API
  const handleFallbackDownload = async () => {
    // Chrome without HTTPS has the annoying "keep" prompt for each file
    if (isChrome && !window.isSecureContext) {
      alert(
        'Bulk "Download All to Folder" requires a secure (HTTPS) connection in Chrome.\n\n' +
        'Please access this site via HTTPS to enable this feature, ' +
        'or download files individually using the download buttons on each file card.'
      );
      return;
    }

    // Firefox and other browsers: sequential downloads with measured bandwidth
    // Start session keep-alive to prevent token expiration during long downloads
    startSessionKeepAlive();
    try {
      setDownloadProgress({ status: 'fetching', current: 0, total: 0, filename: '', error: '' });

      const response = await fetch(`/api/v1/observations/${observation._id}/download-urls`);
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired — please log in again');
        }
        throw new Error(`Failed to get download URLs (${response.status})`);
      }
      const result = await response.json();
      const data = result.data;

      if (data.files.length === 0) {
        stopSessionKeepAlive();
        setDownloadProgress({ status: 'complete', current: 0, total: 0, filename: '', error: '' });
        return;
      }

      // Download first file via fetch to measure bandwidth
      const firstFile = data.files[0];
      setDownloadProgress({
        status: 'downloading',
        current: 1,
        total: data.files.length,
        filename: `${firstFile.filename} (measuring speed...)`,
        error: ''
      });

      const startTime = performance.now();
      // Minted download-proxy URLs now require auth; attach the token since
      // neither a header-less fetch nor an <a> navigation can send a header.
      const firstResponse = await fetch(withAuthToken(firstFile.url), { credentials: 'same-origin' });
      if (!firstResponse.ok) {
        throw new Error(`Failed to download first file: ${firstResponse.status}`);
      }
      const firstBlob = await firstResponse.blob();
      const endTime = performance.now();

      // Calculate bandwidth in bytes per millisecond
      const downloadTimeMs = endTime - startTime;
      const bytesPerMs = firstBlob.size / downloadTimeMs;
      const mbPerSec = (bytesPerMs * 1000) / (1024 * 1024);
      console.log(`Measured bandwidth: ${mbPerSec.toFixed(2)} MB/s (${firstBlob.size} bytes in ${downloadTimeMs.toFixed(0)}ms)`);

      // Trigger download of first file via blob URL
      const blobUrl = URL.createObjectURL(firstBlob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = firstFile.filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      console.log(`First file downloaded, starting remaining ${data.files.length - 1} files`);

      // If only one file, we're done
      if (data.files.length === 1) {
        stopSessionKeepAlive();
        setDownloadProgress({ status: 'complete', current: 1, total: 1, filename: '', error: '' });
        return;
      }

      // Download remaining files - trigger each, then wait for it to complete
      for (let i = 1; i < data.files.length; i++) {
        const file = data.files[i];

        // Update progress to show current file starting
        setDownloadProgress({
          status: 'downloading',
          current: i + 1,
          total: data.files.length,
          filename: file.filename,
          error: ''
        });

        // Trigger download via anchor element
        const fileLink = document.createElement('a');
        fileLink.href = withAuthToken(file.url);
        fileLink.download = file.filename;
        fileLink.style.display = 'none';
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);

        // Calculate delay based on measured bandwidth + 20% buffer, then wait
        const estimatedTimeMs = (file.size || firstBlob.size) / bytesPerMs * 1.2;
        const delayMs = Math.ceil(estimatedTimeMs);

        console.log(`Waiting ${delayMs}ms for ${file.filename} (${((file.size || 0) / (1024 * 1024)).toFixed(1)} MB)`);

        // Show waiting message
        setDownloadProgress({
          status: 'downloading',
          current: i + 1,
          total: data.files.length,
          filename: `${file.filename} - waiting ${Math.round(delayMs / 1000)}s...`,
          error: ''
        });

        await new Promise(resolve => setTimeout(resolve, delayMs));
      }

      stopSessionKeepAlive();
      setDownloadProgress({ status: 'complete', current: data.files.length, total: data.files.length, filename: '', error: '' });
    } catch (error) {
      stopSessionKeepAlive();
      console.error('Fallback download failed:', error);
      setDownloadProgress({ status: 'error', current: 0, total: 0, filename: '', error: error.message || 'Download failed' });
    }
  };

  // File System Access API download handler with parallel downloads
  // selectedIds (a Set of file_ids) limits the batch to a chosen subset (#1275);
  // null downloads every file. Only ever called with a subset from the Chromium-only
  // multi-select bar, so the fallback branch below never carries a subset.
  const handleDownloadAll = async (format = 'raw', selectedIds = null) => {
    // Check browser support
    if (!supportsDirectoryPicker) {
      await handleFallbackDownload();
      return;
    }

    // Create abort controller for cancellation
    downloadAbortController.current = new AbortController();
    const signal = downloadAbortController.current.signal;

    // Concurrency limit for parallel downloads (lower for calibrated to reduce server load)
    const CONCURRENCY_LIMIT = format === 'calibrated' ? 2 : 4;

    try {
      // Step 1: Let user pick directory
      const dirHandle = await window.showDirectoryPicker({
        mode: 'readwrite',
        startIn: 'downloads'
      });

      // Start session keep-alive to prevent token expiration during long downloads
      startSessionKeepAlive();

      // Step 2: Get presigned URLs from server
      setDownloadProgress({ status: 'fetching', current: 0, total: 0, filename: '', error: '' });

      const token = getAccessToken();
      const response = await fetch(`/api/v1/observations/${observation._id}/download-urls`, {
        signal,
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired — please log in again');
        }
        throw new Error(`Failed to get download URLs (${response.status})`);
      }
      const result = await response.json();
      const data = result.data;
      // #1275: a subset selection downloads only those file_ids; otherwise all files.
      const files = selectedIds ? data.files.filter(f => selectedIds.has(f.file_id)) : data.files;

      // Step 3: Download files with parallel processing (4 at a time)
      setDownloadProgress({ status: 'downloading', current: 0, total: files.length, filename: '', error: '' });

      let completedCount = 0;
      const inProgressFiles = new Set();
      // #1276: frames the server couldn't calibrate come back raw (X-Calibration-Status:
      // unavailable) — collect them so we can warn once the batch finishes.
      const uncalibrated = [];

      // Helper to download and save a single file
      const downloadFile = async (file, index) => {
        if (signal.aborted) {
          throw new DOMException('Download cancelled', 'AbortError');
        }

        inProgressFiles.add(file.filename);
        console.log(`[${index + 1}/${files.length}] Fetching: ${file.filename}`);

        try {
          // For calibrated downloads, use the calibration endpoint per file.
          // Otherwise file.url is a minted download-proxy URL (now authenticated)
          // — attach the token for the header-less fetch below.
          let downloadUrl = withAuthToken(file.url);
          if (format === 'calibrated' && file.file_id) {
            const token = getAccessToken();
            downloadUrl = `/api/v1/calibration/download/${file.file_id}?format=both&token=${encodeURIComponent(token)}`;
          }

          const fileResponse = await fetch(downloadUrl, { credentials: 'same-origin', signal });
          if (!fileResponse.ok) {
            console.error(`Failed to download ${file.filename}: ${fileResponse.status}`);
            return false;
          }

          const blob = await fileResponse.blob();
          console.log(`[${index + 1}/${files.length}] Fetched ${file.filename}: ${blob.size} bytes`);

          // Use filename from Content-Disposition if available (calibrated files get _calibrated suffix)
          let saveFilename = file.filename;
          const contentDisposition = fileResponse.headers.get('Content-Disposition');
          if (contentDisposition) {
            const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
            if (match) saveFilename = match[1];
          }

          // #1276: a `format=both` request returns the RAW frame with
          // X-Calibration-Status: unavailable when it can't be calibrated. Mark it so
          // it's never mistaken for calibrated data, and warn once at the end.
          if (format === 'calibrated'
              && isUncalibratedResponse(fileResponse.headers.get('X-Calibration-Status'))) {
            uncalibrated.push(saveFilename);
            saveFilename = markUncalibratedName(saveFilename);
          }

          // Write to selected directory
          const fileHandle = await dirHandle.getFileHandle(saveFilename, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();

          console.log(`[${index + 1}/${files.length}] Saved ${file.filename}`);
          return true;
        } catch (err) {
          if (err.name === 'AbortError') {
            throw err;
          }
          console.error(`Error with ${file.filename}:`, err);
          return false;
        } finally {
          inProgressFiles.delete(file.filename);
        }
      };

      // Process files in batches of CONCURRENCY_LIMIT
      for (let i = 0; i < files.length; i += CONCURRENCY_LIMIT) {
        if (signal.aborted) {
          stopSessionKeepAlive();
          setDownloadProgress({ status: 'cancelled', current: completedCount, total: files.length, filename: '', error: '' });
          return;
        }

        // Get the next batch of files
        const batch = files.slice(i, i + CONCURRENCY_LIMIT);
        const batchNames = batch.map(f => f.filename.split('.')[0]).join(', ');

        setDownloadProgress({
          status: 'downloading',
          current: completedCount,
          total: files.length,
          filename: `Downloading: ${batchNames}...`,
          error: ''
        });

        // Download batch in parallel
        try {
          const results = await Promise.all(
            batch.map((file, batchIndex) => downloadFile(file, i + batchIndex))
          );

          // Count successes
          completedCount += results.filter(Boolean).length;

          setDownloadProgress({
            status: 'downloading',
            current: completedCount,
            total: files.length,
            filename: `${completedCount} of ${files.length} completed...`,
            error: ''
          });
        } catch (err) {
          if (err.name === 'AbortError') {
            stopSessionKeepAlive();
            setDownloadProgress({ status: 'cancelled', current: completedCount, total: files.length, filename: '', error: '' });
            return;
          }
          // Continue with next batch even if some files failed
          console.error('Batch error:', err);
        }
      }

      setDownloadProgress({ status: 'complete', current: completedCount, total: files.length, filename: '', error: '' });
      const uncalibratedWarning = buildUncalibratedWarning(uncalibrated);
      if (uncalibratedWarning) setDownloadError(uncalibratedWarning);
    } catch (error) {
      if (error.name === 'AbortError') {
        // User cancelled via abort controller or directory picker
        stopSessionKeepAlive();
        setDownloadProgress({ status: 'cancelled', current: downloadProgress.current, total: downloadProgress.total, filename: '', error: '' });
        return;
      }
      console.error('Download failed:', error);
      setDownloadProgress({ status: 'error', current: 0, total: 0, filename: '', error: error.message || 'Download failed' });
    } finally {
      stopSessionKeepAlive();
      downloadAbortController.current = null;
    }
  };

  // Cancel download handler
  const handleCancelDownload = () => {
    if (downloadAbortController.current) {
      downloadAbortController.current.abort();
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !observation) {
    return (
      <Container>
        <Box mt={4}>
          <Alert severity="error">{error || 'Observation not found'}</Alert>
          <Box mt={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(backRoute)}
            >
              {backLabel}
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  const Wrapper = embedded ? React.Fragment : Container;
  const wrapperProps = embedded ? {} : { maxWidth: 'lg' };

  return (
    <Wrapper {...wrapperProps}>
      <Box mt={embedded ? 0 : 4} mb={embedded ? 0 : 4}>
        {/* Breadcrumbs — hidden in embedded mode */}
        {!embedded && (
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 3 }}>
          <Button
            color="inherit"
            onClick={() => navigate(backRoute)}
            sx={{ textDecoration: 'none', color: 'inherit', minWidth: 'auto', p: 0 }}
          >
            {fromPage === 'dashboard' ? 'Dashboard' : fromPage === 'admin' ? 'Admin Observations' : 'Observations'}
          </Button>
          <Typography color="text.primary">
            {observation.name || observation.targets?.[0]?.name || 'Observation Details'}
          </Typography>
        </Breadcrumbs>
        )}

        {/* Header */}
        <Box display="flex" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" flexGrow={1}>
            {observation.name || observation.targets?.[0]?.name || 'Unknown Target'}
          </Typography>
          <Chip
            label={observation.status === 'waiting_next' ? `Next: Run ${(observation.series_stats?.total_completed || 0) + 1}` : String(observation.status || '').replace(/_/g, ' ')}
            color={getStatusColor(observation.status)}
            sx={{ textTransform: 'capitalize', mr: 1 }}
          />
          {/* Edit follows the shared editable-states list (waiting_next, failed,
              suspended, expired are editable but NOT cancellable), so it must gate
              on isObservationEditable — the same guard the list edit icon and the
              ObservationEdit load use — not isCancellable, which would hide Edit on
              exactly the halted/retry-able states you most want to fix. */}
          {isObservationEditable(observation.status) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(`/observations/${observation._id}/edit`)}
              startIcon={<EditIcon />}
              sx={{ mr: 1 }}
            >
              Edit
            </Button>
          )}
          {isCancellable(observation.status) && (
            <Button
              variant="outlined"
              size="small"
              color="error"
              onClick={() => setCancelDialogOpen(true)}
              startIcon={<CancelIcon />}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
          )}
          {observation.repetition_config?.enabled && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setExecutionHistoryOpen(true)}
              startIcon={<ScheduleIcon />}
            >
              Execution History
            </Button>
          )}
        </Box>

        {/* Constraint Status */}
        {constraintLoading && (
          <Box mb={2}>
            <LinearProgress sx={{ borderRadius: 1 }} />
          </Box>
        )}
        {constraintStatus && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" mb={1}>
              {constraintStatus.dispatch_status === 'ready' && <CheckCircleIcon color="success" sx={{ mr: 1 }} />}
              {constraintStatus.dispatch_status === 'blocked' && <ErrorIcon color="error" sx={{ mr: 1 }} />}
              {constraintStatus.dispatch_status === 'waiting' && <WarningIcon color="warning" sx={{ mr: 1 }} />}
              <Typography variant="subtitle2">
                Dispatch Status: {constraintStatus.dispatch_status === 'ready' ? 'Ready to schedule' :
                  constraintStatus.dispatch_status === 'blocked' ? 'Blocked' :
                  constraintStatus.dispatch_status === 'waiting' ? 'Waiting for constraints' : 'Unknown'}
              </Typography>
            </Box>
            {constraintStatus.constraint_checks && constraintStatus.constraint_checks.length > 0 && (
              <Box display="flex" flexWrap="wrap" gap={1}>
                {constraintStatus.constraint_checks.map((check, idx) => {
                  const hasNumeric = check.current_value != null;
                  const label = hasNumeric
                    ? `${check.type}: ${Number(check.current_value).toFixed(1)}${check.required_value != null ? ` (req: ${Number(check.required_value).toFixed(1)})` : ''}`
                    : (check.reservation_title
                        ? `${check.type}: ${check.reservation_title}`
                        : (check.message || check.type));
                  return (
                  <Tooltip key={idx} title={check.message || ''} arrow>
                    <Chip
                      size="small"
                      icon={check.status === 'passing' ? <CheckCircleIcon /> : check.blocking ? <ErrorIcon /> : <WarningIcon />}
                      label={label}
                      color={check.status === 'passing' ? 'success' : check.blocking ? 'error' : 'warning'}
                      variant="outlined"
                    />
                  </Tooltip>
                  );
                })}
              </Box>
            )}
            {constraintStatus.next_observable_time && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Next observable: {formatUTC(constraintStatus.next_observable_time)}
              </Typography>
            )}
          </Paper>
        )}

        {/* Failure Alert - Show for failed observations */}
        {observation.status === 'failed' && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            icon={<CancelIcon />}
          >
            <Typography variant="h6" gutterBottom>
              Observation Failed
            </Typography>
            <Typography variant="body1" gutterBottom>
              {observation.error || observation.failure_reason || 'This observation failed to complete. No specific error message was provided.'}
            </Typography>
            {(observation.frames_captured > 0 || observation.expected_file_count > 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Partial completion: {observation.frames_captured || 0} of {observation.expected_file_count || 0} exposures captured
                {observation.captured_files && observation.captured_files.length > 0 &&
                  ` (${observation.captured_files.length} files saved)`
                }
              </Typography>
            )}
            <Box mt={2}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => navigate(`/admin/billing/explorer?observation_id=${observation._id}`)}
                startIcon={<InfoIcon />}
              >
                View Usage Details
              </Button>
            </Box>
          </Alert>
        )}

        {/* Partially Completed Alert - wind-down success (stopped early with usable data) */}
        {observation.status === 'partially_completed' && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            icon={<CheckCircleIcon />}
          >
            <Typography variant="h6" gutterBottom>
              Partially Completed
            </Typography>
            <Typography variant="body1" gutterBottom>
              This observation captured usable data and stopped early
              {observation.partial_completion?.stopped_reason === 'wind_down'
                ? ' to hand the telescope to the next observation.'
                : observation.partial_completion?.stopped_reason
                  ? ` (${String(observation.partial_completion.stopped_reason).replace(/_/g, ' ')}).`
                  : '.'}
            </Typography>
            {(() => {
              const pc = observation.partial_completion || {};
              const done = pc.completed_exposures ?? observation.frames_captured ?? observation.captured_file_count ?? 0;
              const total = pc.total_exposures ?? observation.expected_file_count ?? 0;
              const pct = pc.completion_percentage ?? (total ? Math.round((done / total) * 100) : 0);
              return (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Captured {done} of {total} exposures ({pct}%)
                  {observation.captured_files && observation.captured_files.length > 0 &&
                    ` — ${observation.captured_files.length} files saved`}
                </Typography>
              );
            })()}
            {(observation.started_at || observation.completed_at) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {observation.started_at && `Started ${formatUTC(observation.started_at)}`}
                {observation.started_at && observation.completed_at && ' · '}
                {observation.completed_at && `Ended ${formatUTC(observation.completed_at)}`}
              </Typography>
            )}
          </Alert>
        )}

        {/* Aborted Alert - Show for aborted observations */}
        {observation.status === 'aborted' && (
          <Alert
            severity="warning"
            sx={{ mb: 3 }}
            icon={<CancelIcon />}
          >
            <Typography variant="h6" gutterBottom>
              Observation Aborted
            </Typography>
            <Typography variant="body1" gutterBottom>
              {observation.abort_reason || observation.error || 'This observation was cancelled before completion.'}
            </Typography>
            {(observation.frames_captured > 0 || observation.expected_file_count > 0) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Partial completion: {observation.frames_captured || 0} of {observation.expected_file_count || 0} exposures captured
                {observation.captured_files && observation.captured_files.length > 0 &&
                  ` (${observation.captured_files.length} files saved)`
                }
              </Typography>
            )}
            <Box mt={2}>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                onClick={() => navigate(`/admin/billing/explorer?observation_id=${observation._id}`)}
                startIcon={<InfoIcon />}
              >
                View Usage Details
              </Button>
            </Box>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column - Main Details */}
          <Grid item xs={12} md={8}>
            {/* Target Information */}
            {/* Handle both single target and multi-target observations */}
            {(observation.targets && observation.targets.length > 0 ? observation.targets : [observation.target]).filter(Boolean).map((target, index) => (
              <Paper key={index} sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <ScienceIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      {observation.targets && observation.targets.length > 1 ? `Target ${index + 1}: ${target.name}` : `Target: ${target.name || 'Unknown'}`}
                    </Typography>
                  </Box>
                  {/* Target Repeat Count */}
                  {target.sequence_repeat_count && target.sequence_repeat_count > 1 && (
                    <Chip 
                      label={`Repeat: ${target.sequence_repeat_count}×`} 
                      size="small" 
                      color="secondary"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Right Ascension
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {target.ra ? decimalToSexagesimal(target.ra, true) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {target.ra ? `${target.ra.toFixed(6)}°` : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Declination
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {target.dec ? decimalToSexagesimal(target.dec, false) : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {target.dec ? `${target.dec.toFixed(6)}°` : ''}
                    </Typography>
                  </Grid>
                  {observation.metadata?.is_fast_mover && (
                    <Grid item xs={12}>
                      <Tooltip title="Uses fine-grained 5-minute scheduling resolution for fast-moving objects like NEOs">
                        <Chip
                          label="Fast Mover (NEO)"
                          color="error"
                          size="small"
                          icon={<ScheduleIcon />}
                        />
                      </Tooltip>
                    </Grid>
                  )}
                  {target.type && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Object Type
                      </Typography>
                      <Typography variant="body1">
                        {target.type}
                      </Typography>
                    </Grid>
                  )}
                  {target.magnitude && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Magnitude
                      </Typography>
                      <Typography variant="body1">
                        {target.magnitude}
                      </Typography>
                    </Grid>
                  )}
                  {target.selected_instrument && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Selected Instrument
                      </Typography>
                      <Typography variant="body1">
                        {target.selected_instrument}
                        {target.instrument_port && ` (Port ${target.instrument_port})`}
                      </Typography>
                    </Grid>
                  )}
                  {observation.selected_instrument && !target.selected_instrument && index === 0 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Selected Instrument
                      </Typography>
                      <Typography variant="body1">
                        {observation.selected_instrument}
                        {observation.instrument_port && ` (Port ${observation.instrument_port})`}
                      </Typography>
                    </Grid>
                  )}
                  
                  {/* Target Visibility Information */}
                  {(target.visibility || calculatedVisibilities[index]) && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        <LocationIcon sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
                        Visibility for {target.name}
                      </Typography>
                      <Grid container spacing={2}>
                        {(() => {
                          const visibility = target.visibility || calculatedVisibilities[index];
                          return (
                            <>
                              {visibility.rise_time && (
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="caption" color="text.secondary">
                                    Rise
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(visibility.rise_time).toLocaleString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false,
                                      timeZone: 'UTC'
                                    })} UTC
                                  </Typography>
                                </Grid>
                              )}
                              {visibility.transit_time && (
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="caption" color="text.secondary">
                                    Transit
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(visibility.transit_time).toLocaleString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false,
                                      timeZone: 'UTC'
                                    })} UTC
                                    {visibility.transit_altitude && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        Alt: {visibility.transit_altitude.toFixed(1)}°
                                      </Typography>
                                    )}
                                  </Typography>
                                </Grid>
                              )}
                              {visibility.set_time && (
                                <Grid item xs={12} sm={4}>
                                  <Typography variant="caption" color="text.secondary">
                                    Set
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(visibility.set_time).toLocaleString('en-US', {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: false,
                                      timeZone: 'UTC'
                                    })} UTC
                                  </Typography>
                                </Grid>
                              )}
                              {visibility.hours_visible !== undefined && (
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary">
                                    Visible for {visibility.hours_visible.toFixed(1)} hours
                                  </Typography>
                                </Grid>
                              )}
                              {visibility.is_circumpolar && (
                                <Grid item xs={12}>
                                  <Chip label="Circumpolar" size="small" color="success" />
                                </Grid>
                              )}
                              {visibility.never_rises && (
                                <Grid item xs={12}>
                                  <Chip label="Never Rises" size="small" color="warning" />
                                </Grid>
                              )}
                            </>
                          );
                        })()}
                      </Grid>
                    </Grid>
                  )}
                  
                  {/* Exposure Information for this target */}
                  {target.exposure_configs && target.exposure_configs.length > 0 && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                        Exposures for {target.name}
                      </Typography>
                      {target.exposure_configs.map((exp, expIndex) => {
                        const perSequenceCount = exp.exposure_count || exp.count || 1;
                        const repeatCount = target.sequence_repeat_count || 1;
                        const totalCount = perSequenceCount * repeatCount;
                        const label = repeatCount > 1
                          ? `${exp.filter || 'No Filter'}: ${totalCount} × ${exp.exposure_time || exp.exposureTime || 0}s (${perSequenceCount}/seq × ${repeatCount} repeats)`
                          : `${exp.filter || 'No Filter'}: ${perSequenceCount} × ${exp.exposure_time || exp.exposureTime || 0}s`;
                        return (
                        <Box key={expIndex} sx={{ ml: 2, mb: 1 }}>
                          <Chip
                            size="small"
                            label={label}
                            color="primary"
                            variant="outlined"
                            sx={{ mr: 1 }}
                          />
                          {exp.binning && (
                            <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                              Binning: {exp.binning}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Gain: {exp.gain != null && exp.gain !== 0 ? exp.gain : 'Auto'}, Offset: {exp.offset != null && exp.offset !== 0 ? exp.offset : 'Auto'}
                            {exp.readout_mode ? `, Readout: ${exp.readout_mode}` : ''}
                          </Typography>
                        </Box>
                        );
                      })}
                    </Grid>
                  )}
                </Grid>
              </Paper>
            ))}

            {/* Global Repeat Count */}
            {observation.global_repeat_count && observation.global_repeat_count > 1 && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <TimerIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Global Repeat</Typography>
                </Box>
                <Typography variant="body1">
                  All targets will be observed <strong>{observation.global_repeat_count} times</strong> in sequence
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Each complete sequence includes all targets with their individual repeat counts
                </Typography>
              </Paper>
            )}

            {/* Timing Information for Time-based Observations */}
            {observation.observation_type === 'time_based' && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <ScheduleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Observation Window</Typography>
                </Box>
                <Grid container spacing={2}>
                  {observation.constraints?.start_time && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Observation Start (UTC) {observation.metadata?.observation_type === 'exoplanet_transit' && observation.constraints?.baseline_before ? `[Ingress - ${observation.constraints.baseline_before} min]` : ''}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(observation.constraints.start_time).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                          timeZone: 'UTC',
                          timeZoneName: 'short'
                        })}
                      </Typography>
                    </Grid>
                  )}
                  {observation.constraints?.end_time && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Observation End (UTC) {observation.metadata?.observation_type === 'exoplanet_transit' && observation.constraints?.baseline_after ? `[Egress + ${observation.constraints.baseline_after} min]` : ''}
                      </Typography>
                      <Typography variant="body1">
                        {new Date(observation.constraints.end_time).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false,
                          timeZone: 'UTC',
                          timeZoneName: 'short'
                        })}
                      </Typography>
                    </Grid>
                  )}
                  {observation.estimated_duration_minutes && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Duration
                      </Typography>
                      <Typography variant="body1">
                        {Math.floor(observation.estimated_duration_minutes / 60)}h {observation.estimated_duration_minutes % 60}m
                      </Typography>
                      {/* #734: how the estimate was derived (measured cadence vs fallback constants) */}
                      {observation.estimated_duration_breakdown && (() => {
                        const b = observation.estimated_duration_breakdown;
                        const parts = [
                          b.exposure_minutes != null && `${b.exposure_minutes}m exposures`,
                          b.overhead_minutes != null && `${b.overhead_minutes}m overhead`,
                          b.setup_minutes != null && `${b.setup_minutes}m setup`,
                          b.buffer_minutes ? `${b.buffer_minutes}m buffer` : null,
                        ].filter(Boolean);
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {parts.join(' · ')}
                            {b.source === 'measured'
                              ? ' (measured from this observatory’s realized durations)'
                              : ' (estimated — not enough history yet)'}
                          </Typography>
                        );
                      })()}
                    </Grid>
                  )}
                  {/* Show transit event times for exoplanet observations */}
                  {observation.metadata?.observation_type === 'exoplanet_transit' && (
                    <>
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }}>
                          <Chip label="Fixed Astronomical Events" size="small" />
                        </Divider>
                      </Grid>
                      {observation.metadata?.transit_ingress && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Transit Ingress (UTC)
                          </Typography>
                          <Typography variant="body1" color="primary">
                            {new Date(observation.metadata.transit_ingress).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZone: 'UTC',
                              timeZoneName: 'short'
                            })}
                          </Typography>
                        </Grid>
                      )}
                      {observation.metadata?.transit_midpoint && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Transit Midpoint (UTC)
                          </Typography>
                          <Typography variant="body1" color="primary">
                            {new Date(observation.metadata.transit_midpoint).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZone: 'UTC',
                              timeZoneName: 'short'
                            })}
                          </Typography>
                        </Grid>
                      )}
                      {observation.metadata?.transit_egress && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="body2" color="text.secondary">
                            Transit Egress (UTC)
                          </Typography>
                          <Typography variant="body1" color="primary">
                            {new Date(observation.metadata.transit_egress).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit',
                              hour12: false,
                              timeZone: 'UTC',
                              timeZoneName: 'short'
                            })}
                          </Typography>
                        </Grid>
                      )}
                    </>
                  )}
                  {observation.constraints?.minimum_altitude && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Minimum Altitude
                      </Typography>
                      <Typography variant="body1">
                        {observation.constraints.minimum_altitude}°
                      </Typography>
                    </Grid>
                  )}
                  {observation.constraints?.is_time_critical && (
                    <Grid item xs={12}>
                      <Chip
                        label="Time Critical"
                        color="warning"
                        size="small"
                        icon={<ScheduleIcon />}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Project Information */}
            {project && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <InfoIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Project Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Project Name
                    </Typography>
                    <Typography variant="body1">
                      {project.name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Project Code
                    </Typography>
                    <Typography variant="body1">
                      {project.code}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Type
                    </Typography>
                    <Chip 
                      label={project.type} 
                      size="small" 
                      color="primary" 
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Status
                    </Typography>
                    <Chip 
                      label={project.status} 
                      size="small" 
                      color={project.status === 'active' ? 'success' : 'default'}
                    />
                  </Grid>
                  {project.scientific_goals && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Scientific Goals
                      </Typography>
                      <Typography variant="body1">
                        {project.scientific_goals}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </Paper>
            )}

            {/* Overall Exposure Summary - if exposures are not per-target */}
            {observation.exposures && observation.exposures.length > 0 && (!observation.targets || observation.targets.every(t => !t.exposure_configs || t.exposure_configs.length === 0)) && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <CameraIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Exposure Details</Typography>
                </Box>
                <List>
                  {observation.exposures.map((exposure, index) => (
                    <ListItem key={index} divider={index < observation.exposures.length - 1}>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={2}>
                            <Chip
                              icon={<FilterIcon />}
                              label={exposure.filter || 'No Filter'}
                              size="small"
                              color="primary"
                            />
                            <Typography variant="body1">
                              {exposure.exposure_count || exposure.count || 1} × {exposure.exposure_time || exposure.duration || 0}s
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Binning: {exposure.binning || '1x1'}
                            </Typography>
                            {exposure.selected_instrument && (
                              <Chip
                                label={`${exposure.selected_instrument}${exposure.instrument_port ? ` (Port ${exposure.instrument_port})` : ''}`}
                                size="small"
                                variant="outlined"
                                color="secondary"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          `Gain: ${exposure.gain != null && exposure.gain !== 0 ? exposure.gain : 'Auto'}, Offset: ${exposure.offset != null && exposure.offset !== 0 ? exposure.offset : 'Auto'}${exposure.readout_mode ? `, Readout: ${exposure.readout_mode}` : ''}`
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}

            {/* Observation Log */}
            <ObservationLog observationId={id} />

            {/* Files */}
            {(obsFiles.length > 0 || (observation.files && observation.files.length > 0)) && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center">
                    <InfoIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">
                      Captured Files ({(() => {
                        // #1040: break the count out by file_type so raw captures
                        // aren't conflated with derived (calibrated/…) files.
                        const raw = fileTypeCounts?.raw;
                        if (fileTypeCounts && raw != null) {
                          const calibrated = fileTypeCounts.calibrated || 0;
                          const other = Object.entries(fileTypeCounts)
                            .filter(([k]) => k !== 'raw' && k !== 'calibrated')
                            .reduce((s, [, v]) => s + (v || 0), 0);
                          const parts = [`${raw.toLocaleString()} images`];
                          // #1323: calibrated companions are no longer listed as their
                          // own rows (one row per science frame); they're downloadable via
                          // "Download Calibrated". Label the count as "available" so it
                          // reads as a capability, not a separate file set.
                          if (calibrated) parts.push(`${calibrated.toLocaleString()} calibrated available`);
                          if (other) parts.push(`${other.toLocaleString()} other`);
                          return parts.join(' · ');
                        }
                        return (obsFiles.length || observation.files?.length || 0).toLocaleString();
                      })()})
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {supportsDirectoryPicker && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadAll('raw')}
                      >
                        Download All
                      </Button>
                    )}
                    {/* #686: offer the server-streamed ZIP in EVERY browser, beside
                        the folder picker where it exists — Chrome/Edge users also get
                        the robust one-file download, not just the per-file loop. */}
                    <ZipDownloadButton
                      obsId={observation?._id}
                      format="raw"
                      fileCount={zipCaps?.rawCount ?? obsFiles.length}
                      totalBytes={zipCaps?.rawBytes ?? 0}
                      maxFiles={zipCaps?.maxFiles}
                      maxBytes={zipCaps?.maxBytes}
                      label="Download All (zip)"
                      icon={<DownloadIcon />}
                    />
                    {obsFiles.some(f => f.calibratable) && (
                      <>
                        {supportsDirectoryPicker && (
                          <Tooltip title="Downloads calibrated where masters available, raw otherwise">
                            <Button
                              variant="outlined"
                              size="small"
                              color="success"
                              startIcon={<ScienceIcon />}
                              onClick={() => handleDownloadAll('calibrated')}
                            >
                              Download Calibrated
                            </Button>
                          </Tooltip>
                        )}
                        {/* #686: calibrated streaming ZIP in every browser too. */}
                        <ZipDownloadButton
                          obsId={observation?._id}
                          format="calibrated"
                          fileCount={zipCaps?.rawCount ?? obsFiles.length}
                          totalBytes={zipCaps?.rawBytes ?? 0}
                          maxFiles={zipCaps?.maxFiles}
                          maxBytes={zipCaps?.maxBytes}
                          label="Download Calibrated (zip)"
                          icon={<ScienceIcon />}
                          color="success"
                        />
                      </>
                    )}
                    {(!observation?.external_storage_destinations?.length
                      || observation.external_storage_destinations.some(d => typeof d === 'object' && d.delete_from_minio !== true)) && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<FolderOpenIcon />}
                        onClick={() => navigate(`/observations/${observation._id}/files`)}
                      >
                        Manage Files
                      </Button>
                    )}
                    {(obsFiles.length > 0 || observation.files?.length > 0) && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloudUploadIcon />}
                        onClick={() => setPushDialogOpen(true)}
                      >
                        Push Externally
                      </Button>
                    )}
                    {obsFiles.some(f => !f.preview_url) && (
                      <Tooltip title="Reprocess images that are missing preview thumbnails">
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<RetryIcon />}
                          onClick={async () => {
                            try {
                              const token = getAccessToken();
                              const resp = await fetch(`/api/v1/processing/reprocess-missing-previews/${observation._id}`, {
                                method: 'POST',
                                headers: { 'Authorization': token ? `Bearer ${token}` : '' },
                              });
                              const data = await resp.json();
                              if (data.success) {
                                alert(`Submitted ${data.data.submitted} reprocessing jobs for ${data.data.total_missing} images missing previews`);
                                // Poll every 3 s so previews update progressively (#614).
                                loadObservation({ silent: true, continuePolling: true });
                              } else {
                                alert(`Failed: ${data.error?.message || 'Unknown error'}`);
                              }
                            } catch (e) {
                              alert(`Error: ${e.message}`);
                            }
                          }}
                        >
                          Reprocess Missing Previews ({obsFiles.filter(f => !f.preview_url).length})
                        </Button>
                      </Tooltip>
                    )}
                    {obsFiles.length > 0 && (
                      <Tooltip title="Regenerate previews from raw FITS. Use after a calibration-master fix to replace previews damaged by stale masters (issue #598).">
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          startIcon={<RetryIcon />}
                          onClick={() => setRebuildPreviewsOpen(true)}
                        >
                          Rebuild Previews
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                {multiSelect && (
                  <ImageSelectionBar
                    sel={sel}
                    busy={downloadProgress.status === 'fetching' || downloadProgress.status === 'downloading'}
                    onDownloadRaw={() => handleDownloadAll('raw', sel.selectedIds)}
                    onDownloadCalibrated={() => handleDownloadAll('calibrated', sel.calibratableSelectedIds)}
                  />
                )}
                <Grid container spacing={2}>
                  {renderedFiles.map((file, index) => {
                    // Cache-bust on preview_updated_at (only changes when the PNG
                    // is (re)written) instead of updated_at (changes on every
                    // UserFile save). Using updated_at caused the browser to abort
                    // the in-flight preview fetch every time an unrelated save
                    // (plate-solve, quality metrics) rolled updated_at forward
                    // during a rebuild, so previews only appeared once polling
                    // stopped (#614).
                    // #1287: prefer the cache-stable signed URL (falls back to token when unsigned).
                    const previewUrl = file.preview_url ? filePreviewUrl(file) : null;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={file.file_id || index}>
                        <Card
                          sx={{
                            position: 'relative',
                            ...(multiSelect && sel.isSelected(file.file_id) && {
                              outline: '2px solid', outlineColor: 'primary.main',
                            }),
                          }}
                        >
                          {multiSelect && sel.isSelected(file.file_id) && (
                            <CheckCircleIcon
                              color="primary"
                              sx={{ position: 'absolute', top: 4, left: 4, zIndex: 2, bgcolor: 'background.paper', borderRadius: '50%' }}
                            />
                          )}
                          {/* Preview + metadata is the selection click target (Chrome-only multi-select) */}
                          <Box
                            onClick={multiSelect ? (e) => sel.handleCardClick(index, e) : undefined}
                            sx={multiSelect ? { cursor: 'pointer', userSelect: 'none' } : undefined}
                          >
                          {previewUrl ? (
                            <Box sx={{ position: 'relative' }}>
                              <LazyImage
                                // Lazy-load previews so a large observation
                                // doesn't fire one request per frame on open
                                // (#925). Cache-bust already keyed on
                                // preview_updated_at above.
                                src={previewUrl}
                                height={140}
                                alt={file.filename || file.file_name}
                                sx={{ objectFit: 'contain' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                              <Box sx={{ position: 'absolute', top: 4, right: 4, opacity: 0.9 }}>
                                <FileCalibrationChip file={file} />
                                <ProcessingFailureChip file={file} />
                              </Box>
                            </Box>
                          ) : (
                            <Box height={140} display="flex" alignItems="center" justifyContent="center" bgcolor="grey.200">
                              <Typography color="text.secondary">No Preview</Typography>
                            </Box>
                          )}
                          <CardContent sx={{ pb: 1 }}>
                            <Typography variant="body2" noWrap>
                              {(file.filename || file.file_name || file.file_path || '').split('/').pop()}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatFileSize(file.file_size || file.file_size_bytes)} {file.filter && file.filter !== 'N/A' ? `• ${file.filter}` : ''} {file.exposure_time ? `• ${file.exposure_time}s` : ''}
                            </Typography>
                          </CardContent>
                          </Box>
                          <CardActions sx={{ pt: 0 }}>
                            <Tooltip title="View Info">
                              <IconButton size="small" onClick={() => openFileDetail(file)}>
                                <InfoIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Download Raw">
                              <IconButton size="small" onClick={async () => {
                                const filePath = file.file_path || file.file_id;
                                const filename = (file.filename || file.file_name || filePath || '').split('/').pop();
                                const url = withAuthToken(`/api/v1/fits/download-proxy/${encodeURIComponent(filePath)}?filename=${encodeURIComponent(filename)}`);
                                try {
                                  const response = await fetch(url);
                                  if (!response.ok) {
                                    const body = await response.json().catch(() => ({}));
                                    throw new Error(body.error || `Download failed (${response.status})`);
                                  }
                                  const blob = await response.blob();
                                  const blobUrl = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = blobUrl; a.download = filename; a.click();
                                  URL.revokeObjectURL(blobUrl);
                                } catch (err) {
                                  setDownloadError(err.message || 'Download failed');
                                }
                              }}>
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {file.calibratable && (
                              <Tooltip title="Download Calibrated">
                                <IconButton size="small" color="success" onClick={async () => {
                                  const token = getAccessToken();
                                  const url = `/api/v1/calibration/download/${file.file_id}?format=calibrated&token=${encodeURIComponent(token)}`;
                                  try {
                                    const response = await fetch(url);
                                    if (!response.ok) {
                                      const json = await response.json().catch(() => ({}));
                                      setCalibrateError(json.error || 'Calibration failed');
                                      return;
                                    }
                                    const blob = await response.blob();
                                    const contentDisposition = response.headers.get('Content-Disposition');
                                    let filename = `calibrated_${file.file_id}.fits`;
                                    if (contentDisposition) {
                                      const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
                                      if (match) filename = match[1];
                                    }
                                    const blobUrl = URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = blobUrl;
                                    a.download = filename;
                                    a.click();
                                    URL.revokeObjectURL(blobUrl);
                                  } catch (err) {
                                    setCalibrateError(err.message || 'Download failed');
                                  }
                                }}>
                                  <ScienceIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </CardActions>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            )}
          </Grid>

          {/* Right Column - Metadata */}
          <Grid item xs={12} md={4}>
            {/* Scheduling Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <ScheduleIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Scheduling</Typography>
              </Box>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Priority"
                    secondary={observation.priority || 'Normal'}
                  />
                </ListItem>
                {observation.observation_type && (
                  <ListItem>
                    <ListItemText
                      primary="Observation Type"
                      secondary={
                        observation.observation_type === 'time_based' ? 'Time-Based' :
                        observation.observation_type === 'fixed_time' ? 'Fixed Time' :
                        observation.observation_type === 'monitoring' ? 'Monitoring' :
                        'Flexible'
                      }
                    />
                  </ListItem>
                )}
                {observation.observation_type === 'time_based' && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Observing Duration"
                        secondary={(() => {
                          // For time-based observations, show the estimated duration
                          const duration = observation.estimated_duration_minutes || 
                                         observation.desired_observing_time_minutes || 
                                         observation.minimum_observing_time_minutes || 0;
                          if (duration > 0) {
                            const hours = Math.floor(duration / 60);
                            const minutes = Math.floor(duration % 60);
                            return hours > 0 ? `${hours}h ${minutes}m` : `${minutes} minutes`;
                          }
                          return 'Not specified';
                        })()}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Estimated Image Count"
                        secondary={(() => {
                          // Calculate estimated image count based on available time
                          const duration = observation.estimated_duration_minutes || 
                                         observation.desired_observing_time_minutes || 
                                         observation.minimum_observing_time_minutes || 0;
                          
                          if (duration <= 0) return 'N/A';
                          
                          // Get exposure configurations
                          const exposureConfigs = [];
                          
                          // Check for exposures at different levels
                          if (observation.targets && observation.targets.length > 0) {
                            observation.targets.forEach(target => {
                              if (target.exposure_configs && target.exposure_configs.length > 0) {
                                exposureConfigs.push(...target.exposure_configs);
                              }
                            });
                          }
                          
                          if (exposureConfigs.length === 0 && observation.exposures) {
                            exposureConfigs.push(...observation.exposures);
                          }
                          
                          if (exposureConfigs.length === 0 && observation.targets?.[0]?.exposure_configs) {
                            exposureConfigs.push(...observation.targets[0].exposure_configs);
                          }
                          
                          if (exposureConfigs.length === 0) return 'No exposures configured';
                          
                          // Calculate average exposure time and overhead
                          let totalExposureTime = 0;
                          let exposureCount = 0;
                          
                          exposureConfigs.forEach(config => {
                            const expTime = config.exposure_time || config.exposureTime || 0;
                            if (expTime > 0) {
                              totalExposureTime += expTime;
                              exposureCount++;
                            }
                          });
                          
                          if (exposureCount === 0) return 'No valid exposures';
                          
                          const avgExposureTime = totalExposureTime / exposureCount;
                          
                          // Add overhead for download, dithering, filter changes
                          // Typical overhead: 5-10 seconds for download, 5 seconds for dither, 10 seconds for filter change
                          const downloadOverhead = 7; // seconds per image
                          const ditherOverhead = observation.guiding?.dither_enabled ? 5 : 0; // seconds if dithering
                          const filterChangeOverhead = exposureConfigs.length > 1 ? 5 : 0; // Average filter change time
                          
                          const totalOverheadPerImage = downloadOverhead + ditherOverhead + filterChangeOverhead;
                          const totalTimePerImage = avgExposureTime + totalOverheadPerImage;
                          
                          const availableSeconds = duration * 60;
                          const estimatedImages = Math.floor(availableSeconds / totalTimePerImage);
                          
                          // Build detailed breakdown
                          const parts = [];
                          parts.push(`~${estimatedImages} images`);
                          parts.push(`(${avgExposureTime.toFixed(0)}s exposure + ${totalOverheadPerImage}s overhead)`);
                          
                          return parts.join(' ');
                        })()}
                      />
                    </ListItem>
                    {observation.actual_observing_time_minutes > 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Actual Observing Time"
                          secondary={(() => {
                            const duration = observation.actual_observing_time_minutes;
                            const hours = Math.floor(duration / 60);
                            const minutes = Math.floor(duration % 60);
                            return hours > 0 ? `${hours}h ${minutes.toFixed(1)}m` : `${minutes.toFixed(1)} minutes`;
                          })()}
                        />
                      </ListItem>
                    )}
                  </>
                )}
                {observation.observation_type === 'fixed_time' && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Fixed Start Time"
                        secondary={
                          observation.fixed_time_start
                            ? formatUTC(observation.fixed_time_start)
                            : 'Not set'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Fixed End Time"
                        secondary={
                          observation.fixed_time_end
                            ? formatUTC(observation.fixed_time_end)
                            : 'Not set'
                        }
                      />
                    </ListItem>
                  </>
                )}
                {observation.observation_type === 'monitoring' && (
                  <>
                    <ListItem>
                      <ListItemText
                        primary="Monitoring Cadence"
                        secondary={`Every ${observation.cadence_days || 1} day${observation.cadence_days !== 1 ? 's' : ''}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Monitoring Period"
                        secondary={
                          <>
                            {observation.cadence_start_date
                              ? formatUTC(observation.cadence_start_date, 'PP')
                              : 'Not set'}
                            {' to '}
                            {observation.cadence_end_date
                              ? formatUTC(observation.cadence_end_date, 'PP')
                              : 'Ongoing'}
                          </>
                        }
                      />
                    </ListItem>
                    {observation.cadence_max_observations && (
                      <ListItem>
                        <ListItemText
                          primary="Maximum Observations"
                          secondary={
                            <>
                              {`${observation.cadence_observations_completed || 0} of ${observation.cadence_max_observations} completed`}
                              {observation.cadence_observations_failed > 0 && 
                                ` (${observation.cadence_observations_failed} failed)`
                              }
                            </>
                          }
                        />
                      </ListItem>
                    )}
                    {!observation.cadence_max_observations && observation.cadence_observations_completed > 0 && (
                      <ListItem>
                        <ListItemText
                          primary="Observations"
                          secondary={
                            <>
                              {`${observation.cadence_observations_completed || 0} completed`}
                              {observation.cadence_observations_failed > 0 && 
                                `, ${observation.cadence_observations_failed} failed`
                              }
                            </>
                          }
                        />
                      </ListItem>
                    )}
                    {observation.cadence_last_observed && (
                      <ListItem>
                        <ListItemText
                          primary="Last Observed"
                          secondary={formatUTC(observation.cadence_last_observed)}
                        />
                      </ListItem>
                    )}
                    {observation.cadence_next_eligible && (
                      <ListItem>
                        <ListItemText
                          primary="Next Eligible"
                          secondary={formatUTC(observation.cadence_next_eligible)}
                        />
                      </ListItem>
                    )}
                  </>
                )}
                <ListItem>
                  <ListItemText
                    primary="Created"
                    secondary={
                      observation.created_at
                        ? formatUTC(observation.created_at)
                        : 'Unknown'
                    }
                  />
                </ListItem>
                {(observation.institution_id || observation.institution_display) && (
                  <ListItem>
                    <ListItemText
                      primary="Institution"
                      secondary={
                        observation.institution_id?.name || 
                        observation.institution_display?.name || 
                        'Unknown'
                      }
                    />
                  </ListItem>
                )}
                {(observation.created_by) && (
                  <ListItem>
                    <ListItemText
                      primary="Observer"
                      secondary={
                        observation.created_by?.first_name && observation.created_by?.last_name
                          ? `${observation.created_by.first_name} ${observation.created_by.last_name}`
                          : observation.created_by?.username || 'Unknown'
                      }
                    />
                  </ListItem>
                )}
                {observation.scheduled_start && (
                  <ListItem>
                    <ListItemText
                      primary="Scheduled Start"
                      secondary={formatUTC(observation.scheduled_start)}
                    />
                  </ListItem>
                )}
                {observation.completed_at && (
                  <ListItem>
                    <ListItemText
                      primary="Completed"
                      secondary={formatUTC(observation.completed_at)}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>

            {/* Observatory Information */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Observatory</Typography>
              </Box>
              {(observation.assigned_observatory || observation.assigned_to) ? (
                <>
                  <Typography variant="body2">
                    {observation.assigned_observatory?.name || observation.assigned_to?.name || 'Unknown Observatory'}
                    {(observation.assigned_observatory?.status || observation.assigned_to?.status) === 'inactive' && (
                      <Typography component="span" variant="body2" color="error" sx={{ ml: 1, fontWeight: 'bold' }}>
                        INACTIVE
                      </Typography>
                    )}
                  </Typography>
                  {(observation.assigned_observatory?.location || observation.assigned_to?.location) && (
                    <Typography variant="caption" color="text.secondary">
                      {(() => {
                        const loc = observation.assigned_observatory?.location || observation.assigned_to?.location;
                        if (typeof loc === 'string') return loc;
                        if (loc && typeof loc === 'object') {
                          return `${loc.city || ''} ${loc.country || ''} (Lat: ${loc.latitude?.toFixed(4)}, Lon: ${loc.longitude?.toFixed(4)})`.trim();
                        }
                        return '';
                      })()}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not assigned to an observatory
                </Typography>
              )}
            </Paper>

            {/* External Storage Destinations */}
            {(observation.external_storage_destinations?.length > 0 || storageInfo.configs.length > 0) && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <CloudUploadIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">External Storage</Typography>
                </Box>
                {storageInfo.loading ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (() => {
                  const deliveries = buildDeliveries(storageInfo.configs, storageInfo.transfers);
                  if (deliveries.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        No external transfers yet for this observation.
                      </Typography>
                    );
                  }
                  const retryTransfer = async (transferId) => {
                    try {
                      await apiService.retryExternalStorageTransfer(transferId);
                      const res = await apiService.getExternalStorageTransfers({ observation_id: observation._id, limit: 2000 });
                      setStorageInfo(prev => ({ ...prev, transfers: res.data?.transfers || res.data || [] }));
                    } catch (err) {
                      console.error('Failed to retry transfer:', err);
                    }
                  };
                  return deliveries.map((d, idx) => {
                    const allComplete = d.total > 0 && d.completed === d.total;
                    return (
                      <Box key={d.key} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                          <Box display="flex" alignItems="center" gap={0.75}>
                            <Typography variant="body2" fontWeight="medium">{d.displayName}</Typography>
                            <Tooltip title={d.origin === 'initial'
                              ? 'Delivered automatically as part of the initial observation'
                              : 'Pushed on demand via Push Externally'}>
                              <Chip
                                label={d.origin === 'initial' ? 'Initial' : 'On-demand'}
                                color={d.origin === 'initial' ? 'default' : 'info'}
                                size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Tooltip>
                          </Box>
                          <Chip
                            label={PROVIDER_LABELS[d.provider] || d.provider || '—'}
                            color={PROVIDER_COLORS[d.provider] || 'default'}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                          {d.directory || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                          {d.origin === 'initial'
                            ? 'Part of the initial observation'
                            : `Pushed${d.requestedBy ? ` by ${d.requestedBy}` : ''}${d.requestedAt ? ` · ${formatUTC(d.requestedAt, 'yyyy-MM-dd HH:mm')}` : ''}`}
                        </Typography>
                        {d.active > 0 && (
                          <LinearProgress
                            variant="determinate"
                            value={d.total > 0 ? (d.completed / d.total) * 100 : 0}
                            sx={{ mb: 0.5, borderRadius: 1 }}
                          />
                        )}
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="caption" color="text.secondary">
                            {d.completed}/{d.total} complete
                          </Typography>
                          {allComplete && <Chip label="Complete" color="success" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                          {d.active > 0 && <Chip label={`${d.active} in progress`} color="info" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                          {d.failedTransfers.length > 0 && <Chip label={`${d.failedTransfers.length} failed`} color="error" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />}
                          {d.cancelled > 0 && <Chip label={`${d.cancelled} cancelled`} color="default" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />}
                        </Box>
                        {d.failedTransfers.map((transfer) => (
                          <Box key={transfer._id} display="flex" alignItems="center" gap={1} mt={0.5}>
                            <Chip label="Failed" color="error" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                            <Typography variant="caption" color="error">
                              {transfer.file_name}{transfer.last_error ? `: ${transfer.last_error}` : ''}
                            </Typography>
                            <Tooltip title="Retry transfer">
                              <IconButton size="small" color="primary" onClick={() => retryTransfer(transfer._id)}>
                                <RetryIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ))}
                        {idx < deliveries.length - 1 && <Divider sx={{ mt: 2 }} />}
                      </Box>
                    );
                  });
                })()}
              </Paper>
            )}

            {/* Repetitive Observation Configuration */}
            {observation.repetition_config?.enabled && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <ScheduleIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Repetition Configuration</Typography>
                </Box>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Interval"
                      secondary={(() => {
                        const days = observation.repetition_config.interval_days || 0;
                        const hours = observation.repetition_config.interval_hours || 0;
                        if (days > 0 && hours > 0) {
                          return `Every ${days} day${days !== 1 ? 's' : ''} and ${hours} hour${hours !== 1 ? 's' : ''}`;
                        } else if (days > 0) {
                          return `Every ${days} day${days !== 1 ? 's' : ''}`;
                        } else {
                          return `Every ${hours} hour${hours !== 1 ? 's' : ''}`;
                        }
                      })()}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Start Date"
                      secondary={formatUTC(observation.repetition_config.start_date)}
                    />
                  </ListItem>
                  {observation.repetition_config.end_date && (
                    <ListItem>
                      <ListItemText
                        primary="End Date"
                        secondary={formatUTC(observation.repetition_config.end_date)}
                      />
                    </ListItem>
                  )}
                  {observation.series_stats && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Progress"
                          secondary={`${observation.series_stats.total_completed || 0} of ${observation.series_stats.total_planned || 0} completed`}
                        />
                      </ListItem>
                      {observation.series_stats.success_rate !== undefined && observation.series_stats.total_completed > 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Success Rate"
                            secondary={`${(observation.series_stats.success_rate * 100).toFixed(1)}%`}
                          />
                        </ListItem>
                      )}
                      {observation.series_stats.total_failed > 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Failed"
                            secondary={`${observation.series_stats.total_failed} execution${observation.series_stats.total_failed !== 1 ? 's' : ''}`}
                          />
                        </ListItem>
                      )}
                      {observation.series_stats.total_skipped > 0 && (
                        <ListItem>
                          <ListItemText
                            primary="Skipped"
                            secondary={`${observation.series_stats.total_skipped} execution${observation.series_stats.total_skipped !== 1 ? 's' : ''}`}
                          />
                        </ListItem>
                      )}
                    </>
                  )}
                  {observation.next_execution && (
                    <>
                      <ListItem>
                        <ListItemText
                          primary="Next Execution"
                          secondary={formatUTC(observation.next_execution.ideal_time)}
                        />
                      </ListItem>
                      {observation.next_execution.window_start && observation.next_execution.window_end && (
                        <ListItem>
                          <ListItemText
                            primary="Execution Window"
                            secondary={`${formatUTC(observation.next_execution.window_start, 'HH:mm')} - ${formatUTC(observation.next_execution.window_end, 'HH:mm')}`}
                          />
                        </ListItem>
                      )}
                    </>
                  )}
                  {observation.repetition_config.window_hours && (
                    <ListItem>
                      <ListItemText
                        primary="Window"
                        secondary={`±${observation.repetition_config.window_hours} hours around ideal time`}
                      />
                    </ListItem>
                  )}
                  {observation.repetition_config.max_executions && (
                    <ListItem>
                      <ListItemText
                        primary="Maximum Executions"
                        secondary={observation.repetition_config.max_executions}
                      />
                    </ListItem>
                  )}
                  {observation.repetition_config.skip_if_delayed_hours && (
                    <ListItem>
                      <ListItemText
                        primary="Skip If Delayed"
                        secondary={`More than ${observation.repetition_config.skip_if_delayed_hours} hours`}
                      />
                    </ListItem>
                  )}
                  {observation.repetition_config.priority_boost_per_day && (
                    <ListItem>
                      <ListItemText
                        primary="Priority Boost"
                        secondary={`+${observation.repetition_config.priority_boost_per_day} per day delayed`}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            )}

            {/* Settings */}
            {observation.settings && (
              <Paper sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <SettingsIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Settings</Typography>
                </Box>
                <List dense>
                  {observation.settings.auto_focus !== undefined && (
                    <ListItem>
                      <ListItemText
                        primary="Auto Focus"
                        secondary={observation.settings.auto_focus ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                  )}
                  {observation.settings.meridian_flip !== undefined && (
                    <ListItem>
                      <ListItemText
                        primary="Meridian Flip"
                        secondary={observation.settings.meridian_flip ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                  )}
                  {observation.settings.guiding !== undefined && (
                    <ListItem>
                      <ListItemText
                        primary="Guiding"
                        secondary={observation.settings.guiding ? 'Enabled' : 'Disabled'}
                      />
                    </ListItem>
                  )}
                </List>
              </Paper>
            )}

            {/* Autofocus Configuration */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <SettingsIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Autofocus</Typography>
              </Box>
              {(() => {
                  const af = observation.autofocus_options || {};
                  const triggers = af.trigger_overrides || {};
                  const hasOverrides = af.enabled !== undefined || af.initial_autofocus !== undefined || Object.keys(triggers).length > 0;

                  if (!hasOverrides) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        Using observatory defaults
                      </Typography>
                    );
                  }

                  return (
                    <List dense>
                      {af.enabled !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Plugin-managed autofocus"
                            secondary={af.enabled ? 'Enabled' : 'Disabled (external AF mode)'}
                          />
                        </ListItem>
                      )}
                      {af.initial_autofocus !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Initial autofocus"
                            secondary={af.initial_autofocus ? 'Yes' : 'No'}
                          />
                        </ListItem>
                      )}
                      {triggers.time_based?.enabled !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Time-based AF"
                            secondary={triggers.time_based.enabled
                              ? `Every ${triggers.time_based.interval_minutes || 30} minutes`
                              : 'Disabled'}
                          />
                        </ListItem>
                      )}
                      {triggers.temperature_based?.enabled !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="Temperature-based AF"
                            secondary={triggers.temperature_based.enabled
                              ? `Threshold: ${triggers.temperature_based.threshold_celsius || 2}°C`
                              : 'Disabled'}
                          />
                        </ListItem>
                      )}
                      {triggers.hfr_based?.enabled !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="HFR-based AF"
                            secondary={triggers.hfr_based.enabled
                              ? `${triggers.hfr_based.increase_percentage || 5}% increase`
                              : 'Disabled'}
                          />
                        </ListItem>
                      )}
                      {triggers.filter_change?.enabled !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="AF on filter change"
                            secondary={triggers.filter_change.enabled ? 'Enabled' : 'Disabled'}
                          />
                        </ListItem>
                      )}
                      {triggers.after_exposures?.enabled !== undefined && (
                        <ListItem>
                          <ListItemText
                            primary="AF after N exposures"
                            secondary={triggers.after_exposures.enabled
                              ? `Every ${triggers.after_exposures.exposure_count || 10} exposures`
                              : 'Disabled'}
                          />
                        </ListItem>
                      )}
                    </List>
                  );
                })()}
              </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* File Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <ViewIcon sx={{ mr: 1 }} />
            File Details
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedFile && (
            <Box>
              {/* Basic Information */}
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
                        Filename
                      </TableCell>
                      <TableCell>{selectedFile.file_name || selectedFile.filename || (selectedFile.file_path ? selectedFile.file_path.split('/').pop() : 'Unknown')}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                        File Size
                      </TableCell>
                      <TableCell>
                        {formatFileSize(selectedFile.file_size_bytes || selectedFile.file_size || selectedFile.size || 0)}
                      </TableCell>
                    </TableRow>
                    {(selectedFile.fits_header || selectedFile.filter) && (
                      <>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Object
                          </TableCell>
                          <TableCell>{selectedFile.fits_header?.OBJECT || selectedFile.target_name || 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Filter
                          </TableCell>
                          <TableCell>
                            <Chip label={selectedFile.filter || selectedFile.fits_header?.FILTER || 'Unknown'} size="small" color="primary" />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Exposure Time
                          </TableCell>
                          <TableCell>{(selectedFile.exposure_time || selectedFile.fits_header?.EXPTIME) ? `${selectedFile.exposure_time || selectedFile.fits_header?.EXPTIME} seconds` : 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Binning
                          </TableCell>
                          <TableCell>{selectedFile.binning || (selectedFile.fits_header?.XBINNING ? `${selectedFile.fits_header.XBINNING}x${selectedFile.fits_header.YBINNING || selectedFile.fits_header.XBINNING}` : 'Unknown')}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                            Image Type
                          </TableCell>
                          <TableCell>{selectedFile.image_type || selectedFile.fits_header?.IMAGETYP || 'Unknown'}</TableCell>
                        </TableRow>
                        {(selectedFile.target_ra != null || selectedFile.fits_header?.RA != null) && (selectedFile.target_dec != null || selectedFile.fits_header?.DEC != null) && (
                          <>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Coordinates (RA/Dec)
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace' }}>
                                {decimalToSexagesimal(selectedFile.target_ra ?? selectedFile.fits_header?.RA, true)} / {decimalToSexagesimal(selectedFile.target_dec ?? selectedFile.fits_header?.DEC, false)}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                                Coordinates (Decimal)
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                                {(selectedFile.target_ra ?? selectedFile.fits_header?.RA)?.toFixed(6)}° / {(selectedFile.target_dec ?? selectedFile.fits_header?.DEC)?.toFixed(6)}°
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        {(selectedFile.fits_header?.['DATE-OBS']) && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Observation Date
                            </TableCell>
                            <TableCell>{formatUTC(selectedFile.fits_header['DATE-OBS'])}</TableCell>
                          </TableRow>
                        )}
                        {selectedFile.fits_header?.AIRMASS && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Airmass
                            </TableCell>
                            <TableCell>{Number(selectedFile.fits_header.AIRMASS).toFixed(3)}</TableCell>
                          </TableRow>
                        )}
                        {selectedFile.fits_header?.TELESCOP && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Telescope
                            </TableCell>
                            <TableCell>{selectedFile.fits_header.TELESCOP}</TableCell>
                          </TableRow>
                        )}
                        {(selectedFile.camera_name || selectedFile.fits_header?.INSTRUME) && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Instrument
                            </TableCell>
                            <TableCell>{selectedFile.camera_name || selectedFile.fits_header?.INSTRUME}</TableCell>
                          </TableRow>
                        )}
                        {selectedFile.fits_header?.OBSERVER && (
                          <TableRow>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                              Observer
                            </TableCell>
                            <TableCell>{selectedFile.fits_header.OBSERVER}</TableCell>
                          </TableRow>
                        )}
                      </>
                    )}
                    {selectedFile.checksum && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Checksum
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                          {selectedFile.checksum}
                        </TableCell>
                      </TableRow>
                    )}
                    {selectedFile.uploaded_at && (
                      <TableRow>
                        <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                          Uploaded
                        </TableCell>
                        <TableCell>
                          {formatUTC(selectedFile.uploaded_at)}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Detail (fits_header/quality_metrics) is fetched on open */}
              {detailLoading && !selectedFile.fits_header && !selectedFile.quality_metrics && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mb: 3 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">Loading header and quality data…</Typography>
                </Box>
              )}

              {/* Quality Metrics */}
              {selectedFile.quality_metrics && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Image Quality Metrics
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableBody>
                        {buildQualityRows(selectedFile.quality_metrics).map(([label, value]) => (
                          <TableRow key={label}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '40%' }}>
                              {label}
                            </TableCell>
                            <TableCell>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* FITS Header Data */}
              {selectedFile.fits_header && Object.keys(selectedFile.fits_header).length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    FITS Header Data
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableBody>
                        {Object.entries(selectedFile.fits_header)
                          .filter(([key]) => {
                            // Filter out basic keys already shown above
                            const skipKeys = ['FILTER', 'EXPTIME', 'EXPOSURE', 'OBJECT', 'INSTRUME', 
                                            'TELESCOP', 'XBINNING', 'YBINNING', 'RA', 'DEC', 'AIRMASS'];
                            return !skipKeys.includes(key.toUpperCase());
                          })
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%', fontFamily: 'monospace' }}>
                              {key}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                              {typeof value === 'number' ? value.toFixed(6).replace(/\.?0+$/, '') : String(value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}

              {/* Storage Information */}
              {(selectedFile.url || selectedFile.minio_url || selectedFile.file_path) && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Storage Information
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', width: '30%' }}>
                            Storage Path
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
                            {selectedFile.file_path || selectedFile.url || selectedFile.minio_url}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedFile?.s_region && (
            <Button
              startIcon={<PublicIcon />}
              onClick={() => openSkyView(selectedFile)}
            >
              Show on sky
            </Button>
          )}
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Execution History Dialog for repetitive observations */}
      {observation && observation.repetition_config?.enabled && (
        <ExecutionHistory
          observationId={id}
          isOpen={executionHistoryOpen}
          onClose={() => setExecutionHistoryOpen(false)}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => !cancelling && setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CancelIcon sx={{ mr: 1, color: 'error.main' }} />
            Cancel Observation
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to cancel this observation?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              This will stop the observation if it's currently running. The observation status will be changed to "cancelled".
            </Typography>
          </Box>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Cancellation Reason (optional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Enter a reason for cancelling this observation..."
            disabled={cancelling}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCancelDialogOpen(false);
              setCancelReason('');
            }}
            disabled={cancelling}
          >
            Keep Running
          </Button>
          <Button
            onClick={handleCancelObservation}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Observation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rebuild Previews Confirmation Dialog (#598) */}
      <Dialog
        open={rebuildPreviewsOpen}
        onClose={() => !rebuildPreviewsBusy && setRebuildPreviewsOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Rebuild Previews</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This regenerates previews for matching files in this observation. Existing preview
            images are replaced. Raw FITS files are not touched.
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Use this after a calibration-master fix (e.g. issue #600) to replace previews that
            may have been generated against stale or swapped master content.
          </Typography>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={rebuildPreviewsCalibratedOnly}
                onChange={(e) => setRebuildPreviewsCalibratedOnly(e.target.checked)}
                disabled={rebuildPreviewsBusy}
              />
            }
            label="Only rebuild previously-calibrated previews (recommended)"
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            When checked, only files where <code>preview_calibrated: true</code> are rebuilt.
            Uncheck to rebuild every preview in the observation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebuildPreviewsOpen(false)} disabled={rebuildPreviewsBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={rebuildPreviewsBusy}
            startIcon={rebuildPreviewsBusy ? <CircularProgress size={16} /> : <RetryIcon />}
            onClick={async () => {
              setRebuildPreviewsBusy(true);
              try {
                const result = await apiService.rebuildObservationPreviews(observation._id, {
                  calibratedOnly: rebuildPreviewsCalibratedOnly,
                });
                const d = result.data || {};
                alert(
                  `Submitted ${d.submitted || 0} preview-rebuild jobs (matched ${d.matched || 0} files). ` +
                  `New previews will appear as jobs complete.`
                );
                setRebuildPreviewsOpen(false);
                // Poll every 3 s so previews update progressively as each job
                // finishes — the WS debounce alone fires too late for fast batches.
                loadObservation({ silent: true, continuePolling: true });
              } catch (err) {
                const msg = err?.response?.data?.error?.message || err?.response?.data?.error || err.message || 'Rebuild failed';
                alert(`Rebuild failed: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`);
              } finally {
                setRebuildPreviewsBusy(false);
              }
            }}
          >
            {rebuildPreviewsBusy ? 'Submitting...' : 'Rebuild Previews'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Progress Dialog */}
      <Dialog
        open={downloadProgress.status !== 'idle'}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <DownloadIcon sx={{ mr: 1 }} />
            Downloading Files
          </Box>
        </DialogTitle>
        <DialogContent>
          {downloadProgress.status === 'fetching' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={24} />
              <Typography>Preparing download...</Typography>
            </Box>
          )}
          {downloadProgress.status === 'downloading' && (
            <Box sx={{ py: 2 }}>
              <Typography gutterBottom>
                Downloading {downloadProgress.current} of {downloadProgress.total}...
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom noWrap>
                {downloadProgress.filename}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(downloadProgress.current / downloadProgress.total) * 100}
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
          {downloadProgress.status === 'complete' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="success">
                Download complete! {downloadProgress.current} of {downloadProgress.total} files saved.
              </Alert>
            </Box>
          )}
          {downloadProgress.status === 'cancelled' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="warning">
                Download cancelled. {downloadProgress.current} of {downloadProgress.total} files were saved.
              </Alert>
            </Box>
          )}
          {downloadProgress.status === 'error' && (
            <Box sx={{ py: 2 }}>
              <Alert severity="error">
                Error: {downloadProgress.error}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {(downloadProgress.status === 'fetching' || downloadProgress.status === 'downloading') ? (
            <Button onClick={handleCancelDownload} color="warning">
              Cancel
            </Button>
          ) : (
            <Button onClick={() => setDownloadProgress({ status: 'idle', current: 0, total: 0, filename: '', error: '' })}>
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(calibrateError)}
        autoHideDuration={8000}
        onClose={() => setCalibrateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setCalibrateError(null)} severity="warning" variant="filled" sx={{ width: '100%' }}>
          {calibrateError}
        </Alert>
      </Snackbar>
      <Snackbar
        open={Boolean(downloadError)}
        autoHideDuration={6000}
        onClose={() => setDownloadError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setDownloadError(null)} severity="error" variant="filled" sx={{ width: '100%' }}>
          {downloadError}
        </Alert>
      </Snackbar>

      <PushExternallyDialog
        open={pushDialogOpen}
        onClose={() => setPushDialogOpen(false)}
        observationId={observation?._id}
        onQueued={() => setStorageReloadKey(k => k + 1)}
      />
    </Wrapper>
  );
};

export default ObservationDetail;